// Match-quality scoring for the ranking engine.
// PRD: Slice "Ranking Engine" — implements the RESEARCH.md §5.2 quality
// tuple ordering: exact-prefix-same-case > exact-prefix-case-folded >
// CamelCase initials > word-boundary infix > contiguous infix > subsequence
// fuzzy. Values are numeric (lower = better) so the ranker can use a single
// integer compare in its sort.

/** 0 = best (exact-prefix-same-case), 6 = no match. */
export type MatchQuality = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const EXACT_PREFIX_SAME_CASE: MatchQuality = 0;
export const EXACT_PREFIX_CASE_FOLDED: MatchQuality = 1;
export const CAMEL_CASE_INITIALS: MatchQuality = 2;
export const WORD_BOUNDARY_INFIX: MatchQuality = 3;
export const CONTIGUOUS_INFIX: MatchQuality = 4;
export const FUZZY: MatchQuality = 5;
export const NO_MATCH: MatchQuality = 6;

/**
 * Sigils that a candidate label may carry but Monaco's word-definition
 * excludes from the typed word: T-SQL / MySQL locals (`@x`, `@@spid`), Oracle
 * / JDBC binds (`:bind`), PG positional binds (`$1`), T-SQL temp tables
 * (`#tmp`). See ../resolvers/sigilWordRange.ts for the matching replace-range
 * widening.
 */
const SIGIL_CHARS = new Set(['@', ':', '$', '#']);

/** Strip a leading run of sigil characters (`@@spid` → `spid`). */
function stripLeadingSigils(s: string): string {
  let i = 0;
  while (i < s.length && SIGIL_CHARS.has(s.charAt(i))) i += 1;
  return i === 0 ? s : s.slice(i);
}

/**
 * Compute the best match quality between a candidate label and the typed
 * prefix. Empty `typed` is treated as a same-case prefix match (everything
 * matches at quality 0) so manual Ctrl+Space with no prefix preserves
 * tier ordering.
 *
 * SIGIL PARITY: a candidate whose label keeps a leading sigil (`@MinPrice`,
 * `:p_id`, `$1`) is scored a SECOND time with both sides' sigils stripped,
 * and the better (lower) of the two qualities wins. Monaco's SQL word
 * definition treats sigils as separators, so the typed word at `@Mi▮` is
 * `Mi` — against the raw label that is only a CONTIGUOUS_INFIX (4), which
 * {@link rankCandidates}' "drop worse-than-CamelCase when a quality≤2 result
 * exists" filter then discards in favour of the keyword `MIN`. The user
 * typing the local's own name must never lose it: `@MinPrice` vs `Mi` scores
 * 0 on the stripped pair. Taking the MINIMUM keeps this purely additive —
 * nothing that matched before can score worse, and non-sigil labels are
 * untouched.
 */
export function matchQualityFor(label: string, typed: string): MatchQuality {
  const direct = matchQualityCore(label, typed);
  if (direct === EXACT_PREFIX_SAME_CASE) return direct;
  const bareLabel = stripLeadingSigils(label);
  if (bareLabel === label) return direct;
  const bare = matchQualityCore(bareLabel, stripLeadingSigils(typed));
  return bare < direct ? bare : direct;
}

function matchQualityCore(label: string, typed: string): MatchQuality {
  if (typed.length === 0) return EXACT_PREFIX_SAME_CASE;

  if (label.startsWith(typed)) return EXACT_PREFIX_SAME_CASE;

  const labelLower = label.toLowerCase();
  const typedLower = typed.toLowerCase();

  if (labelLower.startsWith(typedLower)) return EXACT_PREFIX_CASE_FOLDED;

  if (matchesCamelCaseInitials(label, typed)) return CAMEL_CASE_INITIALS;

  if (matchesWordBoundaryInfix(label, labelLower, typedLower)) {
    return WORD_BOUNDARY_INFIX;
  }

  if (labelLower.includes(typedLower)) return CONTIGUOUS_INFIX;

  if (matchesSubsequence(labelLower, typedLower)) return FUZZY;

  return NO_MATCH;
}

/**
 * `SOH` matches `SalesOrderHeader`. `gt` matches `getTotal`. Initials are
 * the first char + every char that begins a new word: an uppercase that
 * follows a non-uppercase, or any char that follows `_` / `.` / space.
 * Comparison is case-insensitive against the user's typed string so
 * `soh` also matches `SalesOrderHeader`.
 */
function matchesCamelCaseInitials(label: string, typed: string): boolean {
  if (label.length === 0) return false;
  const initials: string[] = [label[0]!];
  for (let i = 1; i < label.length; i++) {
    const ch = label[i]!;
    const prev = label[i - 1]!;
    if (isUppercase(ch) && !isUppercase(prev)) {
      initials.push(ch);
      continue;
    }
    if (prev === '_' || prev === '.' || prev === ' ') {
      initials.push(ch);
    }
  }
  if (initials.length === 0) return false;
  const initStr = initials.join('').toLowerCase();
  return initStr.startsWith(typed.toLowerCase());
}

/**
 * Typed string is a prefix of any "word" inside the label, where a word
 * boundary is the start of the string, after `_` / `.` / space, or an
 * uppercase letter following a lowercase letter (CamelCase boundary).
 * Comparison is case-insensitive.
 */
function matchesWordBoundaryInfix(
  label: string,
  labelLower: string,
  typedLower: string,
): boolean {
  if (typedLower.length === 0) return true;
  for (let i = 1; i < label.length; i++) {
    const prev = label[i - 1]!;
    const curr = label[i]!;
    const isSeparator = prev === '_' || prev === '.' || prev === ' ';
    const isCamelBoundary = isUppercase(curr) && isLowercase(prev);
    if (isSeparator || isCamelBoundary) {
      if (labelLower.startsWith(typedLower, i)) return true;
    }
  }
  return false;
}

/** Typed chars appear in `label` in order, not necessarily contiguous. */
function matchesSubsequence(label: string, typed: string): boolean {
  if (typed.length === 0) return true;
  let typedIdx = 0;
  for (let labelIdx = 0; labelIdx < label.length && typedIdx < typed.length; labelIdx++) {
    if (label[labelIdx] === typed[typedIdx]) typedIdx++;
  }
  return typedIdx === typed.length;
}

function isUppercase(ch: string): boolean {
  return ch >= 'A' && ch <= 'Z';
}

function isLowercase(ch: string): boolean {
  return ch >= 'a' && ch <= 'z';
}
